/**
 * Minimal Markdown to HTML parser
 * Pure TypeScript, no dependencies
 * 
 * Supports:
 * - Headings (H1-H3)
 * - Bold, Italic, Underline
 * - Color emphasis {text}
 * - Lists
 * - Links
 */

const BRAND_COLOR = '#8B4513'; // Saddle Brown (marron)

/**
 * Convertit Markdown en HTML sécurisé
 */
export function markdownToHtml(markdown: string): string {
  if (!markdown) return '';
  
  const lines = markdown.split('\n');
  const htmlLines: string[] = [];
  let inList = false;
  
  for (const line of lines) {
    const stripped = line.trim();
    
    if (!stripped) {
      // Ligne vide
      if (inList) {
        htmlLines.push('</ul>');
        inList = false;
      }
      htmlLines.push('');
      continue;
    }
    
    // Titres (H1, H2, H3)
    if (stripped.startsWith('###')) {
      const text = stripped.slice(3).trim();
      htmlLines.push(`<h3 class="text-xl font-semibold mb-3 mt-5">${formatInline(text)}</h3>`);
    } else if (stripped.startsWith('##')) {
      const text = stripped.slice(2).trim();
      htmlLines.push(`<h2 class="text-2xl font-semibold mb-4 mt-6">${formatInline(text)}</h2>`);
    } else if (stripped.startsWith('#')) {
      const text = stripped.slice(1).trim();
      htmlLines.push(`<h1 class="text-3xl font-bold mb-5 mt-8">${formatInline(text)}</h1>`);
    }
    // Listes
    else if (stripped.startsWith('- ') || stripped.startsWith('* ')) {
      if (!inList) {
        htmlLines.push('<ul class="list-disc list-inside mb-4 space-y-1">');
        inList = true;
      }
      const text = stripped.slice(2).trim();
      htmlLines.push(`<li class="ml-4">${formatInline(text)}</li>`);
    }
    // Paragraphe normal
    else {
      if (inList) {
        htmlLines.push('</ul>');
        inList = false;
      }
      htmlLines.push(`<p class="mb-4 leading-relaxed">${formatInline(stripped)}</p>`);
    }
  }
  
  // Fermer la liste si ouverte
  if (inList) {
    htmlLines.push('</ul>');
  }
  
  return htmlLines.join('\n');
}

/**
 * Formate le texte inline (gras, italique, etc.)
 * et échappe le HTML pour sécurité
 */
function formatInline(text: string): string {
  // Échapper les caractères HTML dangereux
  let result = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  
  // Gras: **texte**
  result = result.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>');
  
  // Italique: *texte*
  result = result.replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');
  
  // Souligné: ~~texte~~
  result = result.replace(/~~([^~]+)~~/g, '<u class="underline">$1</u>');
  
  // Couleur: {texte}
  result = result.replace(
    /\{([^{}]+)\}/g,
    `<span style="color:${BRAND_COLOR}" class="font-medium">$1</span>`
  );
  
  // Liens: [texte](url)
  result = result.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>'
  );
  
  return result;
}

/**
 * Retire tout le formatage Markdown (pour preview texte brut)
 */
export function stripMarkdown(markdown: string): string {
  if (!markdown) return '';
  
  let text = markdown;
  
  // Retirer les titres #
  text = text.replace(/^#{1,3}\s+/gm, '');
  
  // Retirer les marqueurs de formatage
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');  // **gras**
  text = text.replace(/\*([^*]+)\*/g, '$1');      // *italique*
  text = text.replace(/~~([^~]+)~~/g, '$1');      // ~~souligné~~
  text = text.replace(/\{([^{}]+)\}/g, '$1');     // {couleur}
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');  // [lien](url)
  
  // Retirer les marqueurs de liste
  text = text.replace(/^\s*[-*]\s+/gm, '');
  
  return text.trim();
}
